<script setup lang="ts">
/**
 * Sanitized HTML with the parts that match a search marked — for document
 * bodies and other stored markup. Same matching as `NxHighlight` (accent- and
 * case-insensitive by default; the `query` may be the terms of a text query,
 * and `fold="spelled"` marks exactly what the search found). Hits may cross
 * inline tags and never a block: a word or a phrase ends at a block or a break.
 *
 * The root is a native element (`tag`). `v-html` on `<component>` is rejected
 * by vue/no-v-text-v-html-on-component, and an eslint comment beside that
 * element becomes a second root, so the marked HTML is set with `innerHTML`
 * on the element itself. The string comes from `highlightHtml`, which
 * sanitizes before it parses.
 */
import { computed, h, type VNode } from 'vue';
import type { TextQueryTerm } from '../lib/textQuery';
import type { NxSanitizeOptions } from '../Markdown';
import { highlightHtml } from './highlightHtml';
import type { NxHighlightOptions } from './highlight';

const props = withDefaults(
  defineProps<{
    html?: string | null;
    /** The search: words are marked each on their own, a list of strings, or the terms of a text query. */
    query?: string | readonly string[] | readonly TextQueryTerm[] | null;
    match?: NxHighlightOptions['match'];
    minLength?: number;
    /** For a string query or a list of strings; a `TextQueryTerm` has its own flag. Default `false`. */
    caseSensitive?: boolean;
    /** „Mull“ also marks „Müll“ — part of the `accents` fold. Default `true`. */
    ignoreDiacritics?: boolean;
    /**
     * `accents`: case and accents fall away (`ü` is `u`). `spelled`: the fold of
     * the search, `ü` is `ue` and `ß` is `ss`, with exactly its ranges. Default `accents`.
     */
    fold?: NxHighlightOptions['fold'];
    /** At most this many terms mark (terms that cannot mark do not count). Default 10. */
    maxTerms?: number;
    /** Wrapper element. Default `div`. */
    tag?: string;
    /** Forwarded to the kit sanitizer before matching. */
    sanitize?: NxSanitizeOptions;
  }>(),
  {
    html: '',
    query: '',
    match: 'words',
    minLength: 1,
    caseSensitive: false,
    ignoreDiacritics: true,
    fold: 'accents',
    maxTerms: 10,
    tag: 'div',
    sanitize: () => ({}),
  }
);

const rendered = computed(() =>
  highlightHtml(props.html, props.query, {
    match: props.match,
    minLength: props.minLength,
    caseSensitive: props.caseSensitive,
    ignoreDiacritics: props.ignoreDiacritics,
    fold: props.fold,
    maxTerms: props.maxTerms,
    sanitize: props.sanitize,
  })
);

/** Native root. Fallthrough attrs (class, id, …) land here; the scope id too. */
const Root = (_props: unknown, { attrs }: { attrs: Record<string, unknown> }): VNode =>
  h(props.tag, {
    ...attrs,
    class: ['nx-highlight-html', attrs.class],
    'data-testid': 'nx-highlight-html',
    innerHTML: rendered.value,
  });
</script>

<template>
  <Root />
</template>

<style scoped>
/*
 * Flat selectors on purpose: a `:deep()` nested inside a CSS-nesting block
 * does not match the injected children. The mark class is the same one
 * `NxHighlight` uses; its scoped rule does not reach markup we inject.
 */
.nx-highlight-html :deep(.nx-highlight__mark) {
  padding: 0;
  border-radius: calc(var(--nxt-hairline) * 2);
  /* A hit must stand out from the text around it in both themes. */
  background: color-mix(in srgb, var(--nxt-warning-bg) 38%, transparent);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 1.5)
    color-mix(in srgb, var(--nxt-warning-bg) 38%, transparent);
  color: inherit;
  font-weight: var(--nxt-font-weight-semibold);
}

@media (forced-colors: active) {
  .nx-highlight-html :deep(.nx-highlight__mark) {
    background: Mark;
    color: MarkText;
  }
}
</style>
